<script lang="ts" setup>
import {computed, watch} from 'vue'
import { IonIcon } from '@ionic/vue'
import ButtonToggle from '@/components/ButtonToggle.vue'

type ButtonSegmentButton = {
    key: string
    label?: string,
    icon?: string
}

const props = defineProps<{
    segments: ButtonSegmentButton[],
    value?: string[] | string | undefined,
    default?: string[] | string
    multiple?: boolean,
    allowNoValue?: boolean
}>()

const model = defineModel<string[] | string | undefined>()

const emit = defineEmits<{
    (e: 'update', value: string[] | string | undefined): void
}>()

const current = computed<string[] | string | undefined>(() =>
    props.value !== undefined ? props.value : model.value
)

const setNext = (next: string[] | string | undefined, {initial = false} = {}) => {
    model.value = next
    if (!initial) emit('update', next)
}

let initialized = false

const onClickSegment = (segment: ButtonSegmentButton) => {
    if (props.multiple) {
        const value = (current.value as Array<string> || []).slice() // clone
        const index = value.indexOf(segment.key)

        if (value.length === 1 && index > -1 && !props.allowNoValue) {
            return
        }

        if (index > -1) {
            value.splice(index, 1)
        }
        else {
            value.push(segment.key)
        }

        setNext(value)
        return
    }

    if (segment.key === current.value && !props.allowNoValue) {
        return
    }

    const next =
        current.value === segment.key ? undefined : segment.key
        setNext(next)
}

watch(() => props.segments, () => {
    let value
    
    if (!props.segments || !Array.isArray(props.segments)) {
        return
    }

    if (props.multiple) {
        value = (current.value as Array<string> || []).filter(key => !props.segments.some(segment => segment.key === key))
    }
    else {
        value = props.segments.some(segment => segment.key === current.value)
            ? current.value
            : undefined
    }

    if (!props.allowNoValue) {
        value = props.multiple
            ? Array.isArray(props.default) ? props.default : [props.default || props.segments[0].key]
            : props.default || props.segments[0].key
    }

    // only emit if different from current
    const curr = current.value
    const changed =
        Array.isArray(value) && Array.isArray(curr)
            ? value.length !== curr.length || value.some((k, i) => k !== curr[i])
            : value !== curr
    if (changed) {
        if (!initialized) {
            initialized = true
            setNext(value, { initial: true })
        }else{
            setNext(value)
        }
    }

}, { immediate: true })

</script>

<template>
<div class="button-segment">
    <ButtonToggle
        v-for="segment in segments"
        :key="segment.key"
        externalEventHandling
        :modelValue="
            (!props.multiple && current === segment.key) ||
            (props.multiple && (current as string[]).includes(segment.key))
        "
        @click="onClickSegment(segment)"
    >
        {{ segment.label || segment.key }}
        <ion-icon
            v-if="segment.icon"
            slot="start"
            :icon="segment.icon"
        />
    </ButtonToggle>
</div>
</template>

<style scoped lang="scss">
.button-segment {
    display: flex;
    align-items: center;
    justify-content: space-between;

    background-color: rgba(var(--ion-color-light-rgb), 0.7);
    border-radius: 1em;

    > ion-button {
        margin: 0;
        flex: 1 1 auto;

        &:first-child::part(native) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }

        &:last-child::part(native) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }

        &:not(:first-child):not(:last-child)::part(native) {
            border-radius: 0;
        }

        &:not(.active) {
            --background: transparent;
            --background-hover: transparent;
            --background-activated: transparent;
            --background-focused: transparent;
        }
    }
}
</style>
